import React, { FC } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import DefaultErrorPage from 'next/error';
import { getAllCompanyIDs, getCompanyByID } from '../../../lib/company';
import { useRouter } from 'next/router';
import { decodeCompanyIDHash } from '../../../lib/utils';
import { WWWCompanyDocument } from '../../../../common/documents/WWWCompanyDocument';
import { CompanySkeleton } from '../../../components/company/CompanySkeleton';
import { CompanyPage } from '../../../components/company/CompanyPage';

//
// Company page
// /[lang]/[hash]/[slug]
//

export interface CompanyProps {
  company: WWWCompanyDocument;
}

const Company: FC<CompanyProps> = ({ company: ssrCompany, ...other }) => {
  const { isFallback } = useRouter();

  // Company page has not been generated at build time, waiting for Next.js to generate it now...
  if (isFallback) {
    return <CompanySkeleton />;
  }

  // Company was not found, even after fallback retry
  if (!ssrCompany) {
    return <DefaultErrorPage statusCode={404} />;
  }

  return <CompanyPage company={ssrCompany} />;
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const companyPaths = (await getAllCompanyIDs()) || [];

  /** Combine locale & company paths, this will generate one page for each company in each language */
  const paths = locales.map((locale) => companyPaths.map((company) => ({ params: { ...company.params }, locale }))).flat();

  return {
    paths,
    fallback: true, // TODO: This needs to be implemented right...
  };
};

export const getStaticProps: GetStaticProps<CompanyProps> = async (ctx) => {
  const companyID = decodeCompanyIDHash(ctx.params.hash as string);
  return {
    props: { company: await getCompanyByID(companyID) },
    revalidate: 1, // 60 * 60 * 24, // One day
  };
};

export default Company;
